import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  GraduationCap, 
  Loader2, 
  ArrowRight, 
  ArrowLeft,
  Info,
  CheckCircle,
  Users,
  Shield,
  Mail,
  FileText,
  Phone,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Parent/Guardian Consent Agreement Sections
const PARENT_CONSENT_SECTIONS = {
  consent_authorization: {
    title: "Parental Consent Authorization",
    content: `As the parent or legal guardian of the minor student, I hereby:
• Authorize Gifted Academy Tutors to provide tutoring services to my child
• Confirm that I am the legal parent or guardian with authority to provide this consent
• Agree to supervise my child's use of the platform as appropriate
• Acknowledge that I am responsible for my child's compliance with platform rules
• Agree to maintain open communication with Gifted Academy regarding my child's education`
  },
  student_safety: {
    title: "Student Safety & Supervision",
    content: `Regarding my child's safety:
• I understand that tutoring sessions may occur online or in-person
• I will ensure appropriate supervision during tutoring sessions as needed
• I authorize tutors to communicate with my child for educational purposes
• I will monitor my child's platform activity and communications
• I will report any safety concerns to Gifted Academy immediately
• I understand that Gifted Academy tutors undergo background verification`
  },
  data_consent: {
    title: "Minor Data Collection Consent",
    content: `Regarding my child's personal data:
• I consent to the collection of my child's educational information
• I understand this data is used to provide and improve tutoring services
• I authorize sharing of my child's information with assigned tutors
• I can request access to or deletion of my child's data at any time
• I will be notified of any significant changes to data handling practices
• My child's data will be handled in compliance with child privacy laws`
  },
  payment_responsibility: {
    title: "Payment Responsibility",
    content: `As the responsible party:
• I agree to pay for all tutoring services used by my child
• I understand the payment terms, including refund and cancellation policies
• I am responsible for any charges incurred through my child's account
• I will ensure timely payment for scheduled sessions
• I understand that unpaid balances may result in service suspension`
  },
  emergency_contact: {
    title: "Emergency Contact Authorization",
    content: `In case of emergency:
• I authorize Gifted Academy to contact me using the provided contact information
• I will keep my contact information current and accurate
• I authorize emergency medical treatment if I cannot be reached
• I will notify Gifted Academy of any relevant medical conditions or allergies
• I understand that session recordings may be made for quality and safety purposes`
  },
  liability_release: {
    title: "Liability Release",
    content: `I acknowledge and agree that:
• Gifted Academy is not liable for academic outcomes or grades
• I release Gifted Academy from liability for minor injuries during in-person sessions
• I understand the inherent risks of online and in-person educational activities
• I agree to indemnify Gifted Academy against claims arising from my child's actions
• This agreement is binding and enforceable under applicable law`
  }
};

// Adult Student Agreement Sections
const ADULT_STUDENT_SECTIONS = {
  terms_of_use: {
    title: "Terms of Use",
    content: `By using Gifted Academy Tutors services, you agree to:
• Use the platform for legitimate educational purposes only
• Provide accurate and truthful information in your profile
• Treat tutors and staff with respect and professionalism
• Attend scheduled sessions on time or provide adequate notice for cancellations
• Not share your account credentials with others
• Comply with all applicable laws and regulations`
  },
  tutoring_services: {
    title: "Tutoring Services Agreement",
    content: `You understand and acknowledge that:
• Tutoring sessions are provided by independent contractors, not Gifted Academy employees
• Session quality may vary based on tutor expertise and teaching style
• Results are not guaranteed and depend on student effort and engagement
• Session recordings, if any, are for educational purposes only
• You are responsible for your own learning outcomes and academic progress`
  },
  payment_terms: {
    title: "Payment & Refund Policy",
    content: `Payment terms:
• All sessions must be paid for in advance through our platform
• Refunds are available for cancelled sessions with 24+ hours notice
• No-shows may forfeit session credits
• Package purchases are non-refundable but sessions can be rescheduled
• Pricing is subject to change with notice`
  },
  privacy_policy: {
    title: "Privacy & Data Use",
    content: `Your data and privacy:
• We collect and store your personal information to provide our services
• Your information may be shared with tutors you book sessions with
• We use cookies and analytics to improve our platform
• You can request deletion of your data at any time
• We do not sell your personal information to third parties
• Communication preferences can be managed in your account settings`
  },
  liability: {
    title: "Limitation of Liability",
    content: `Gifted Academy Tutors:
• Is not responsible for academic outcomes or grades
• Does not guarantee tutor availability or session scheduling
• Is not liable for any indirect or consequential damages
• Reserves the right to suspend accounts that violate our terms
• May modify services with reasonable notice to users`
  },
  code_of_conduct: {
    title: "Student Code of Conduct",
    content: `As a student, you agree to:
• Be respectful and professional in all interactions
• Not engage in harassment, discrimination, or inappropriate behavior
• Maintain honesty and academic integrity
• Report any concerns or issues promptly
• Follow platform guidelines and tutor instructions
• Keep your contact information up to date`
  }
};

const AGREEMENT_VERSION = "1.0";

const RegisterPage = () => {
  const navigate = useNavigate();
  
  // Step state
  const [step, setStep] = useState(1);
  
  // Form data
  const [age, setAge] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Parent consent fields
  const [parentName, setParentName] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [parentPhone, setParentPhone] = useState('');
  
  // Agreement state
  const [agreementAcknowledgments, setAgreementAcknowledgments] = useState({});
  const [signature, setSignature] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  
  // Student profile fields
  const [studentGrade, setStudentGrade] = useState('');
  const [currentCourses, setCurrentCourses] = useState('');
  const [schoolName, setSchoolName] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [consentRequired, setConsentRequired] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  
  // Check if under 18
  const isUnder18 = age && parseInt(age) < 18;
  
  // Get the appropriate agreement sections
  const agreementSections = isUnder18 ? PARENT_CONSENT_SECTIONS : ADULT_STUDENT_SECTIONS;
  
  // Initialize acknowledgments when step changes to agreement step
  useEffect(() => {
    if (step === 3) {
      const sections = isUnder18 ? PARENT_CONSENT_SECTIONS : ADULT_STUDENT_SECTIONS;
      const initialAck = {};
      Object.keys(sections).forEach(key => {
        initialAck[key] = false;
      });
      setAgreementAcknowledgments(initialAck);
      setSignature('');
      setExpandedSections({});
    }
  }, [step, isUnder18]);
  
  // Check if all sections are acknowledged
  const allAcknowledged = Object.keys(agreementSections).length > 0 && 
    Object.values(agreementAcknowledgments).every(v => v === true);
  
  // Toggle section expansion
  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Handle next step
  const handleNextStep = () => {
    if (step === 1) {
      // Age validation
      const ageNum = parseInt(age);
      if (!age || isNaN(ageNum) || ageNum < 5 || ageNum > 100) {
        toast.error('Please enter a valid age (5-100)');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Account details validation
      if (!name.trim()) {
        toast.error('Please enter your name');
        return;
      }
      if (!email.trim()) {
        toast.error('Please enter your email');
        return;
      }
      if (password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      
      // Go to agreement step (step 3) for BOTH under-18 and adults
      setStep(3);
    } else if (step === 3) {
      // Agreement validation
      if (isUnder18) {
        // Parent/Guardian info required for under-18
        if (!parentName.trim()) {
          toast.error('Please enter parent/guardian full name');
          return;
        }
        if (!parentEmail.trim()) {
          toast.error('Please enter parent/guardian email');
          return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(parentEmail)) {
          toast.error('Please enter a valid email address');
          return;
        }
        if (!parentPhone.trim()) {
          toast.error('Please enter parent/guardian phone number');
          return;
        }
        const phoneRegex = /^[\d\s\-+()]{7,20}$/;
        if (!phoneRegex.test(parentPhone.replace(/\s/g, ''))) {
          toast.error('Please enter a valid phone number');
          return;
        }
      }
      
      // Check all sections acknowledged
      if (!allAcknowledged) {
        toast.error('Please acknowledge all sections of the agreement');
        return;
      }
      
      // Check signature
      if (!signature.trim()) {
        toast.error('Please type your full legal name as signature');
        return;
      }
      
      // Validate signature matches expected name
      const expectedName = isUnder18 ? parentName : name;
      if (signature.trim().toLowerCase() !== expectedName.trim().toLowerCase()) {
        toast.error(`Signature must match the name: ${expectedName}`);
        return;
      }
      
      setStep(4);
    }
  };

  // Handle registration
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    setLoading(true);
    
    // Log submission attempt for debugging
    console.log('[Signup] Attempting registration for:', email);

    try {
      // Parse courses into array
      const coursesArray = currentCourses
        .split(',')
        .map(c => c.trim())
        .filter(c => c.length > 0);

      const requestBody = {
        name,
        email,
        password,
        role: 'student',
        age: parseInt(age),
        parent_name: isUnder18 ? parentName : null,
        parent_email: isUnder18 ? parentEmail : null,
        parent_phone: isUnder18 ? parentPhone : null,
        parent_consent: isUnder18 ? true : null,
        student_grade: studentGrade || null,
        current_courses: coursesArray,
        school_name: schoolName || null,
        agreement_data: {
          agreement_type: isUnder18 ? 'parent_guardian_consent' : 'adult_student',
          version: AGREEMENT_VERSION,
          signer_type: isUnder18 ? 'parent_guardian' : 'self',
          signer_full_name: isUnder18 ? parentName : name,
          signer_email: isUnder18 ? parentEmail : email,
          signer_phone: isUnder18 ? parentPhone : null,
          typed_signature: signature,
          acknowledgments: agreementAcknowledgments,
          signed_at: new Date().toISOString()
        }
      };

      console.log('[Signup] Sending request to:', `${API_URL}/api/auth/register`);

      let response;
      try {
        response = await fetch(`${API_URL}/api/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });
      } catch (networkError) {
        // Network error (no internet, DNS failure, CORS, etc.)
        console.error('[Signup] Network error:', networkError);
        toast.error('Unable to connect to server. Please check your internet connection and try again.');
        return;
      }

      console.log('[Signup] Response status:', response.status);

      // Read response body
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
          console.log('[Signup] Response data:', data);
        } catch (parseError) {
          console.error('[Signup] JSON parse error:', parseError);
          toast.error('Received invalid response from server. Please try again.');
          return;
        }
      } else {
        // Non-JSON response (HTML error page, etc.)
        const textResponse = await response.text();
        console.error('[Signup] Non-JSON response:', textResponse.substring(0, 200));
        toast.error('Server returned an unexpected response. Please try again later.');
        return;
      }

      if (!response.ok) {
        // Handle specific error codes
        if (response.status === 400) {
          // Validation error - show the specific message
          const errorMessage = typeof data?.detail === 'string' 
            ? data.detail 
            : Array.isArray(data?.detail) 
              ? data.detail[0]?.msg || 'Invalid input. Please check your information.'
              : 'Invalid input. Please check your information.';
          console.warn('[Signup] Validation error:', errorMessage);
          toast.error(errorMessage);
        } else if (response.status === 409 || (data?.detail && data.detail.includes('already'))) {
          toast.error('This email is already registered. Please sign in instead.');
        } else if (response.status === 429) {
          toast.error('Too many attempts. Please wait a moment and try again.');
        } else if (response.status >= 500) {
          console.error('[Signup] Server error:', response.status, data);
          toast.error('Our servers are experiencing issues. Please try again in a few minutes.');
        } else {
          console.error('[Signup] Unknown error:', response.status, data);
          toast.error(data?.detail || 'Registration failed. Please try again.');
        }
        return;
      }

      // Success! Store token
      console.log('[Signup] Registration successful for:', email);
      if (data.token) {
        localStorage.setItem('token', data.token);
      }

      // Check if email verification is required
      if (data.email_verification_required) {
        setEmailVerificationRequired(true);
        setStep(6);
        toast.success('Account created! Please verify your email.');
      } else if (data.consent_required) {
        setConsentRequired(true);
        setStep(5);
        toast.success('Account created! Awaiting parent consent confirmation.');
      } else {
        toast.success('Welcome to Gifted Academy!');
        navigate('/dashboard');
      }
    } catch (error) {
      // Unexpected error
      console.error('[Signup] Unexpected error:', error);
      toast.error('An unexpected error occurred. Please refresh the page and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      toast.success('Verification email sent! Check your inbox.');
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const gradeOptions = [
    { value: 'grade-9', label: 'Grade 9' },
    { value: 'grade-10', label: 'Grade 10' },
    { value: 'grade-11', label: 'Grade 11' },
    { value: 'grade-12', label: 'Grade 12' },
    { value: 'university-1', label: 'University - Year 1' },
    { value: 'university-2', label: 'University - Year 2' },
    { value: 'university-3', label: 'University - Year 3' },
    { value: 'university-4', label: 'University - Year 4' },
    { value: 'graduate', label: 'Graduate Student' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-12" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <GraduationCap className="h-10 w-10 text-indigo-700" />
            <span className="font-heading text-2xl font-semibold text-slate-900">
              Gifted Academy Tutors
            </span>
          </Link>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Create Account</h1>
          <p className="text-slate-600 mt-2">Join our learning community</p>
        </div>

        <Card className="shadow-card border-stone-100">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="font-heading text-xl">
              {step === 1 && 'How old are you?'}
              {step === 2 && 'Your Information'}
              {step === 3 && (isUnder18 ? 'Parent/Guardian Consent Agreement' : 'Student Agreement')}
              {step === 4 && 'Student Profile'}
              {step === 5 && (consentRequired ? 'Almost Done!' : 'Welcome!')}
              {step === 6 && 'Check Your Email'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'We need to know your age for our consent policy'}
              {step === 2 && 'Enter your account details'}
              {step === 3 && (isUnder18 ? 'Parent/guardian must review and sign' : 'Please review and accept our terms')}
              {step === 4 && 'Tell us about your education (optional)'}
              {step === 5 && (consentRequired ? 'Your account is pending consent confirmation' : 'Registration successful!')}
              {step === 6 && 'Verify your email to activate all features'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Step 1: Age Question */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="age">Your Age</Label>
                  <Input
                    id="age"
                    type="number"
                    min="5"
                    max="100"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="Enter your age"
                    className="text-lg text-center"
                    data-testid="age-input"
                  />
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    <strong>Age Policy:</strong> Students under 18 require parent/guardian consent to use our platform. This is in accordance with our <Link to="/consent-policy" className="underline">Parent/Guardian Consent Policy</Link>.
                  </AlertDescription>
                </Alert>

                <Button
                  onClick={handleNextStep}
                  disabled={!age}
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800"
                  data-testid="continue-age"
                >
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <p className="text-center text-sm text-slate-500 mt-4">
                  Already have an account?{' '}
                  <Link to="/login" className="text-indigo-700 font-medium hover:underline">
                    Sign in
                  </Link>
                </p>
              </div>
            )}

            {/* Step 2: Account Details */}
            {step === 2 && (
              <div className="space-y-4">
                {isUnder18 ? (
                  <Alert className="bg-amber-50 border-amber-200">
                    <Users className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800 text-sm">
                      Since you're under 18, parent/guardian consent will be required in the next step.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-blue-50 border-blue-200">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-sm">
                      After entering your details, you'll review and accept our student agreement.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    data-testid="password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    data-testid="confirm-password-input"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="rounded-full"
                    data-testid="back-to-age"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                  <Button
                    onClick={handleNextStep}
                    className="flex-1 rounded-full bg-slate-900 hover:bg-slate-800"
                    data-testid="continue-details"
                  >
                    Continue <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Multi-Section Agreement (for both under-18 and adults) */}
            {step === 3 && (
              <div className="space-y-4">
                {/* Agreement Version Badge */}
                <div className="flex justify-center">
                  <Badge variant="outline" className="px-3 py-1">
                    <FileText className="h-3 w-3 mr-1" />
                    Agreement Version {AGREEMENT_VERSION}
                  </Badge>
                </div>
                
                {/* Context Banner */}
                {isUnder18 ? (
                  <Alert className="bg-amber-50 border-amber-200">
                    <Shield className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800 text-sm">
                      <strong>Parent/Guardian Required:</strong> Since {name} is under 18, a parent or legal guardian must review and sign this consent agreement.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="bg-blue-50 border-blue-200">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800 text-sm">
                      Please review and acknowledge each section of the student agreement below.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Parent/Guardian Contact Info (only for under-18) */}
                {isUnder18 && (
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-slate-600" />
                      <h4 className="font-semibold text-slate-900">Parent/Guardian Information</h4>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="parentName">Full Legal Name <span className="text-red-500">*</span></Label>
                      <Input
                        id="parentName"
                        value={parentName}
                        onChange={(e) => setParentName(e.target.value)}
                        placeholder="Enter your full legal name"
                        data-testid="parent-name-input"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="parentEmail">Email Address <span className="text-red-500">*</span></Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="parentEmail"
                            type="email"
                            value={parentEmail}
                            onChange={(e) => setParentEmail(e.target.value)}
                            placeholder="parent@email.com"
                            className="pl-10"
                            data-testid="parent-email-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="parentPhone">Phone Number <span className="text-red-500">*</span></Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            id="parentPhone"
                            type="tel"
                            value={parentPhone}
                            onChange={(e) => setParentPhone(e.target.value)}
                            placeholder="(123) 456-7890"
                            className="pl-10"
                            data-testid="parent-phone-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Agreement Sections - Scrollable container */}
                <div className="max-h-[300px] overflow-y-auto space-y-3 pr-2" data-testid="agreement-sections">
                  {Object.entries(agreementSections).map(([key, section]) => (
                    <div 
                      key={key}
                      className={`border rounded-xl transition-all ${
                        agreementAcknowledgments[key] 
                          ? 'border-green-200 bg-green-50/50' 
                          : 'border-slate-200 bg-white'
                      }`}
                      data-testid={`agreement-section-${key}`}
                    >
                      {/* Section Header - Clickable to expand */}
                      <button
                        type="button"
                        onClick={() => toggleSection(key)}
                        className="w-full flex items-center justify-between p-4 text-left"
                      >
                        <div className="flex items-center gap-3">
                          {agreementAcknowledgments[key] && (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                          )}
                          <span className={`font-medium ${agreementAcknowledgments[key] ? 'text-green-800' : 'text-slate-900'}`}>
                            {section.title}
                          </span>
                        </div>
                        {expandedSections[key] ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </button>
                      
                      {/* Section Content - Expandable */}
                      {expandedSections[key] && (
                        <div className="px-4 pb-4 border-t border-slate-100">
                          <p className="text-sm text-slate-600 whitespace-pre-line mt-3 mb-4">
                            {section.content}
                          </p>
                          <div className="flex items-center gap-3 pt-3 border-t border-slate-100">
                            <Checkbox
                              id={`ack-${key}`}
                              checked={agreementAcknowledgments[key] || false}
                              onCheckedChange={(checked) => 
                                setAgreementAcknowledgments(prev => ({ ...prev, [key]: checked }))
                              }
                              data-testid={`ack-checkbox-${key}`}
                            />
                            <label htmlFor={`ack-${key}`} className="text-sm font-medium cursor-pointer">
                              I have read and understand this section
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Progress Indicator */}
                <div className="text-center text-sm text-slate-500">
                  Acknowledged: {Object.values(agreementAcknowledgments).filter(Boolean).length} / {Object.keys(agreementSections).length} sections
                </div>

                {/* Digital Signature Section */}
                <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200 space-y-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-indigo-600" />
                    <h4 className="font-semibold text-indigo-900">Digital Signature</h4>
                  </div>
                  
                  <p className="text-sm text-indigo-800">
                    {isUnder18 
                      ? 'Parent/Guardian: By typing your full legal name below, you consent to all terms on behalf of your child.'
                      : 'By typing your full legal name below, you agree to all terms and conditions above.'}
                  </p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signature">Type your full legal name to sign <span className="text-red-500">*</span></Label>
                    <Input
                      id="signature"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder={isUnder18 ? parentName || "Parent/Guardian Full Legal Name" : name || "Your Full Legal Name"}
                      className="text-lg font-medium bg-white"
                      data-testid="signature-input"
                    />
                    <p className="text-xs text-indigo-700">
                      Signature must match: <strong>{isUnder18 ? parentName || 'Parent/Guardian name above' : name}</strong>
                    </p>
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="rounded-full"
                    data-testid="back-to-details"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                  <Button
                    onClick={handleNextStep}
                    disabled={
                      (isUnder18 && (!parentName || !parentEmail || !parentPhone)) ||
                      !allAcknowledged || 
                      !signature.trim()
                    }
                    className="flex-1 rounded-full bg-slate-900 hover:bg-slate-800"
                    data-testid="continue-agreement"
                  >
                    {isUnder18 ? 'Sign Consent' : 'Accept & Continue'} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
                
                <p className="text-center text-xs text-slate-500">
                  A confirmation email will be sent to {isUnder18 ? 'the parent/guardian' : 'your'} email address.
                </p>
              </div>
            )}

            {/* Step 4: Student Profile */}
            {step === 4 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-slate-600 mb-4">
                  This information helps us match you with the right tutors. You can skip or update this later.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="studentGrade">Current Grade/Year</Label>
                  <Select 
                    value={studentGrade} 
                    onValueChange={(value) => {
                      setStudentGrade(value);
                      // Force blur to ensure dropdown closes
                      document.activeElement?.blur();
                    }}
                  >
                    <SelectTrigger data-testid="grade-select" className="relative z-10">
                      <SelectValue placeholder="Select your grade/year" />
                    </SelectTrigger>
                    <SelectContent 
                      position="popper" 
                      sideOffset={4}
                      className="z-50"
                    >
                      {gradeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="schoolName">School Name</Label>
                  <Input
                    id="schoolName"
                    value={schoolName}
                    onChange={(e) => setSchoolName(e.target.value)}
                    placeholder="Enter your school name"
                    data-testid="school-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="currentCourses">Current Courses</Label>
                  <Input
                    id="currentCourses"
                    value={currentCourses}
                    onChange={(e) => setCurrentCourses(e.target.value)}
                    placeholder="e.g., Math, Physics, Chemistry (comma separated)"
                    data-testid="courses-input"
                  />
                  <p className="text-xs text-slate-500">Separate multiple courses with commas</p>
                </div>

                <div className="flex gap-3 pt-2 relative z-20">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(3)}
                    className="rounded-full"
                    data-testid="back-from-profile"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 rounded-full bg-slate-900 hover:bg-slate-800 relative z-20"
                    data-testid="submit-registration"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Account...</>
                    ) : (
                      <>Create Account <ArrowRight className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                </div>

                <p className="text-center text-xs text-slate-500 mt-4">
                  You have already accepted our{' '}
                  <Link to="/terms" className="text-indigo-700 hover:underline">Terms of Use</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-indigo-700 hover:underline">Privacy Policy</Link>
                  {' '}in the previous step.
                </p>
              </form>
            )}

            {/* Step 5: Consent Success */}
            {step === 5 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                
                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900">
                    {consentRequired ? 'Account Created!' : 'Welcome to Gifted Academy!'}
                  </h3>
                  {consentRequired ? (
                    <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-200 text-left">
                      <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-amber-800 font-medium">
                            Pending Consent Confirmation
                          </p>
                          <p className="text-sm text-amber-700 mt-1">
                            Your parent/guardian ({parentName}) has provided initial consent. 
                            You can now access your dashboard, but some features may be limited until consent is fully verified.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-600 mt-2">Your account has been created successfully.</p>
                  )}
                </div>

                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800"
                  data-testid="go-to-dashboard"
                >
                  Go to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Step 6: Email Verification Required */}
            {step === 6 && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto">
                  <Mail className="h-8 w-8 text-indigo-600" />
                </div>
                
                <div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900">
                    Verify Your Email
                  </h3>
                  <p className="text-slate-600 mt-2">
                    We've sent a verification link to:
                  </p>
                  <p className="font-medium text-slate-900 mt-1">{email}</p>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-left">
                  <p className="text-sm text-slate-700">
                    Please check your inbox and click the verification link to activate your account.
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    Can't find it? Check your spam/junk folder.
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  <Button
                    onClick={handleResendVerification}
                    variant="outline"
                    className="w-full rounded-full"
                    disabled={resendingVerification}
                    data-testid="resend-verification"
                  >
                    {resendingVerification ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Resend Verification Email'
                    )}
                  </Button>
                  
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="w-full rounded-full bg-slate-900 hover:bg-slate-800"
                    data-testid="continue-to-dashboard"
                  >
                    Continue to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  
                  <p className="text-xs text-slate-500">
                    You can verify later, but some features may be limited.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default RegisterPage;
